import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Edit, Trash2, Eye, MapPin, Clock, CheckCircle2, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/components/ui/alert-dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const MyListingsPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (user) fetchMyListings();
  }, [user]);

  const fetchMyListings = async () => {
    try {
      const { data } = await axios.get(`${API}/users/${user.id}/listings`, { withCredentials: true });
      setListings(data);
    } catch (error) {
      toast.error('Failed to load listings');
    } finally {
      setLoading(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/listings/${deleteTarget.id}`, { withCredentials: true });
      toast.success('Listing deleted');
      setListings(listings.filter(l => l.id !== deleteTarget.id));
      setDeleteTarget(null);
    } catch (error) {
      const detail = error.response?.data?.detail || 'Failed to delete';
      toast.error(typeof detail === 'string' ? detail : 'Failed to delete');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div style={{ backgroundColor: '#F9F6F0', minHeight: '100vh' }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex justify-between items-center mb-8">
          <h1
            className="text-4xl tracking-tight"
            style={{ fontFamily: 'Cormorant Garamond', fontWeight: 600, color: '#1A1A1A' }}
            data-testid="my-listings-heading"
          >
            My Listings
          </h1>
          <Link to="/post">
            <button
              className="px-6 py-2 rounded-md"
              style={{ backgroundColor: '#D95A41', color: '#FFFFFF', fontFamily: 'Manrope', fontWeight: 600 }}
              data-testid="add-listing-btn"
            >
              Add New Listing
            </button>
          </Link>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <p style={{ fontFamily: 'Manrope', color: '#5C5C5C' }}>Loading...</p>
          </div>
        ) : listings.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="my-listings-grid">
            {listings.map((listing) => {
              const isSoldOut = listing.quantity === 0;
              const status = listing.status || 'approved';
              const statusMeta = {
                pending: { bg: '#FEF3C7', color: '#92400E', label: 'PENDING REVIEW', icon: Clock },
                approved: { bg: '#D1FAE5', color: '#065F46', label: 'LIVE', icon: CheckCircle2 },
                rejected: { bg: '#FEE2E2', color: '#991B1B', label: 'REJECTED', icon: XCircle }
              }[status];
              const StatusIcon = statusMeta.icon;
              return (
                <div
                  key={listing.id}
                  className="overflow-hidden rounded-md border border-[#E5E0D8]"
                  style={{ backgroundColor: '#FFFFFF', position: 'relative' }}
                  data-testid={`my-listing-${listing.id}`}
                >
                  <div
                    className="absolute top-3 left-3 px-2 py-1 rounded flex items-center gap-1 z-10"
                    style={{
                      backgroundColor: statusMeta.bg,
                      color: statusMeta.color,
                      fontFamily: 'Manrope',
                      fontSize: '10px',
                      fontWeight: 700,
                      letterSpacing: '0.1em'
                    }}
                    data-testid={`status-badge-${listing.id}`}
                  >
                    <StatusIcon size={12} strokeWidth={2} />
                    {statusMeta.label}
                  </div>
                  {isSoldOut && (
                    <div
                      className="absolute top-3 right-3 px-2 py-1 rounded z-10"
                      style={{ backgroundColor: '#1A1A1A', color: '#FFFFFF', fontFamily: 'Manrope', fontSize: '10px', fontWeight: 700, letterSpacing: '0.1em' }}
                    >
                      SOLD OUT
                    </div>
                  )}
                  <div className="aspect-[4/3] overflow-hidden" style={{ opacity: isSoldOut || status !== 'approved' ? 0.75 : 1 }}>
                    <img
                      src={listing.images?.[0]
                        ? `${process.env.REACT_APP_BACKEND_URL}${listing.images[0]}`
                        : 'https://images.pexels.com/photos/1640775/pexels-photo-1640775.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940'}
                      alt={listing.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="p-4">
                    <h3
                      className="text-xl mb-2 tracking-tight"
                      style={{ fontFamily: 'Cormorant Garamond', fontWeight: 600, color: '#1A1A1A' }}
                    >
                      {listing.title}
                    </h3>
                    <div className="flex justify-between items-center mb-4">
                      <span
                        className="text-lg"
                        style={{ fontFamily: 'Manrope', fontWeight: 600, color: '#D95A41' }}
                      >
                        ${listing.price}
                      </span>
                      <span
                        className="text-sm"
                        style={{ fontFamily: 'Manrope', color: '#5C5C5C' }}
                      >
                        Qty: {listing.quantity}
                      </span>
                    </div>
                    <div className="flex items-center gap-1 mb-4" style={{ fontFamily: 'Manrope', fontSize: '12px', color: '#5C5C5C' }}>
                      <MapPin size={12} strokeWidth={1.5} />
                      {listing.location}
                    </div>
                    {status === 'rejected' && listing.rejection_reason && (
                      <div
                        className="mb-4 p-2 rounded"
                        style={{ backgroundColor: '#FEE2E2', fontFamily: 'Manrope', fontSize: '12px', color: '#991B1B' }}
                        data-testid={`rejection-reason-${listing.id}`}
                      >
                        <strong>Reason:</strong> {listing.rejection_reason}
                      </div>
                    )}
                    {status === 'pending' && (
                      <div
                        className="mb-4 p-2 rounded"
                        style={{ backgroundColor: '#FEF3C7', fontFamily: 'Manrope', fontSize: '12px', color: '#92400E' }}
                      >
                        Awaiting admin review. You'll be notified once it's live.
                      </div>
                    )}
                    <div className="grid grid-cols-3 gap-2 pt-3 border-t border-[#E5E0D8]">
                      <Button
                        onClick={() => navigate(`/listing/${listing.id}`)}
                        variant="ghost"
                        size="sm"
                        className="rounded-md"
                        style={{ fontFamily: 'Manrope', fontSize: '13px' }}
                        data-testid={`view-listing-${listing.id}`}
                      >
                        <Eye size={14} className="mr-1" />
                        View
                      </Button>
                      <Button
                        onClick={() => navigate(`/edit-listing/${listing.id}`)}
                        variant="outline"
                        size="sm"
                        className="rounded-md"
                        style={{ fontFamily: 'Manrope', fontSize: '13px', borderColor: '#D95A41', color: '#D95A41' }}
                        data-testid={`edit-listing-${listing.id}`}
                      >
                        <Edit size={14} className="mr-1" />
                        Edit
                      </Button>
                      <Button
                        onClick={() => setDeleteTarget(listing)}
                        variant="outline"
                        size="sm"
                        className="rounded-md"
                        style={{ fontFamily: 'Manrope', fontSize: '13px', borderColor: '#991B1B', color: '#991B1B' }}
                        data-testid={`delete-listing-${listing.id}`}
                      >
                        <Trash2 size={14} className="mr-1" />
                        Delete
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-12 p-8 rounded-md border border-[#E5E0D8]" style={{ backgroundColor: '#FFFFFF' }}>
            <p className="mb-4" style={{ fontFamily: 'Manrope', fontSize: '18px', color: '#5C5C5C' }}>
              You haven't posted any listings yet.
            </p>
            <Link to="/post">
              <button
                className="px-6 py-2 rounded-md"
                style={{ backgroundColor: '#D95A41', color: '#FFFFFF', fontFamily: 'Manrope', fontWeight: 600 }}
              >
                Create Your First Listing
              </button>
            </Link>
          </div>
        )}

        <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
          <AlertDialogContent data-testid="delete-confirm-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle style={{ fontFamily: 'Cormorant Garamond', fontWeight: 600 }}>
                Delete this listing?
              </AlertDialogTitle>
              <AlertDialogDescription style={{ fontFamily: 'Manrope' }}>
                This will permanently delete "{deleteTarget?.title}". This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="cancel-delete-btn" style={{ fontFamily: 'Manrope' }}>
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={confirmDelete}
                disabled={deleting}
                style={{ backgroundColor: '#991B1B', fontFamily: 'Manrope', fontWeight: 600 }}
                data-testid="confirm-delete-btn"
              >
                {deleting ? 'Deleting...' : 'Delete'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
};
